import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { MapPin, Shield, Loader2 } from "lucide-react";

const propertyTypes = [
  "retail", "warehouse", "school", "hospital", "residential", "industrial", "office",
  "construction_site", "event_venue", "parking_lot", "gated_community",
  "bank_branch", "credit_union", "financial_institution", "bank_corporate_office", "bank_data_center",
  "government_facility", "military_installation", "embassy_consulate",
  "federal_building", "data_center_scif", "defense_contractor",
  "other"
] as const;

const serviceTypes = [
  { value: "standing_guard", label: "Standing Guard" },
  { value: "mobile_patrol", label: "Mobile Patrol" },
  { value: "fire_watch", label: "Fire Watch" },
  { value: "event_security", label: "Event Security" },
  { value: "alarm_response", label: "Alarm Response" },
  { value: "executive_protection", label: "Executive Protection" },
  { value: "loss_prevention", label: "Loss Prevention" },
  { value: "concierge_security", label: "Concierge / Lobby Security" },
  { value: "construction_site", label: "Construction Site Security" },
  { value: "bank_security", label: "Bank / Financial Institution Security" },
  { value: "bank_armed_guard", label: "Bank Armed Guard Services" },
  { value: "atm_courier", label: "ATM / Courier / Cash-in-Transit" },
  { value: "government_contract", label: "Government Contract Security" },
  { value: "cleared_facility", label: "Cleared Facility Security (DCSA)" },
  { value: "access_control_scif", label: "SCIF / SCI Access Control" },
  { value: "military_base", label: "Military Base Security" },
  { value: "other", label: "Other" },
] as const;

export interface ProposalFormData {
  address: string;
  city: string;
  state: string;
  zip: string;
  propertyType: string;
  serviceType: string;
  notes: string;
}

interface ProposalFormProps {
  onSubmit?: (data: ProposalFormData) => void;
  onChange?: (data: ProposalFormData) => void;
  isLoading?: boolean;
  disabled?: boolean;
  hideSubmit?: boolean;
}

const ProposalForm = ({ onSubmit, onChange, isLoading, disabled, hideSubmit }: ProposalFormProps) => {
  const [form, setForm] = useState<ProposalFormData>({
    address: "",
    city: "",
    state: "",
    zip: "",
    propertyType: "",
    serviceType: "standing_guard",
    notes: "",
  });

  const update = <K extends keyof ProposalFormData>(key: K, value: ProposalFormData[K]) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      onChange?.(next);
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit?.(form);
  };

  return (
    <Card className="border-border bg-card shadow-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-foreground">
          <MapPin className="h-5 w-5 text-primary" />
          Property &amp; Service Information
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-muted-foreground">Street Address</Label>
            <Input value={form.address} onChange={(e) => update("address", e.target.value)} placeholder="123 Main Street" className="mt-1 bg-secondary border-border text-foreground" required />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-muted-foreground">City</Label>
              <Input value={form.city} onChange={(e) => update("city", e.target.value)} placeholder="Miami" className="mt-1 bg-secondary border-border text-foreground" required />
            </div>
            <div>
              <Label className="text-muted-foreground">State</Label>
              <Input value={form.state} onChange={(e) => update("state", e.target.value)} placeholder="FL" className="mt-1 bg-secondary border-border text-foreground" maxLength={2} required />
            </div>
            <div>
              <Label className="text-muted-foreground">ZIP Code</Label>
              <Input value={form.zip} onChange={(e) => update("zip", e.target.value)} placeholder="33101" className="mt-1 bg-secondary border-border text-foreground" required />
            </div>
          </div>

          <div>
            <Label className="text-muted-foreground">Property Type</Label>
            <Select value={form.propertyType} onValueChange={(v) => update("propertyType", v)}>
              <SelectTrigger className="mt-1 bg-secondary border-border text-foreground">
                <SelectValue placeholder="Select property type" />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {propertyTypes.map((t) => (
                  <SelectItem key={t} value={t} className="capitalize">{t.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-muted-foreground">Service Type</Label>
            <Select value={form.serviceType} onValueChange={(v) => update("serviceType", v)}>
              <SelectTrigger className="mt-1 bg-secondary border-border text-foreground">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {serviceTypes.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-muted-foreground">Additional Notes / Special Concerns</Label>
            <Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Previous incidents, high-value assets, access points, etc." className="mt-1 bg-secondary border-border text-foreground min-h-[100px]" />
          </div>

          {!hideSubmit && (
            <Button type="submit" disabled={isLoading || disabled || !form.address || !form.propertyType} className="w-full gradient-primary text-primary-foreground font-semibold h-12 text-base shadow-glow hover:opacity-90 transition-opacity">
              {isLoading ? (
                <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Generating Proposal...</>
              ) : (
                <><Shield className="mr-2 h-5 w-5" /> Generate Security Proposal</>
              )}
            </Button>
          )}
        </form>
      </CardContent>
    </Card>
  );
};

export default ProposalForm;
